import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PageHeader, EmptyState, Field } from "../components/Shared";
import { ProofImage } from "../components/ProofImage";
import { DateField, formatDateID } from "../components/DateField";
import { Eye, EyeOff, ShieldAlert, Loader2, RotateCcw } from "lucide-react";

const EMPTY = { batch_id: "", date_from: "", date_to: "", hidden: "" };

export default function JournalModeration() {
  const { t, lang } = useLang();
  const [access, setAccess] = useState(null);
  const [filters, setFilters] = useState(EMPTY);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/food-journal/access").then(({ data }) => setAccess(data.data)).catch((e) => toast.error(extractError(e).message));
  }, []);

  const load = useCallback(async () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    try {
      const { data } = await api.get(`/food-journal/wall${qs ? `?${qs}` : ""}`);
      setEntries(data.data.entries);
    } catch (e) { toast.error(extractError(e).message); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleHidden = async (entry) => {
    setBusy(true);
    try {
      await api.post(`/food-journal/${entry.entry_id}/hide?hidden=${!entry.is_hidden}`);
      load();
    } catch (e) { toast.error(extractError(e).message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="journal-moderation-page" className="space-y-6">
      <PageHeader eyebrow={t("mod.eyebrow")} title={t("mod.title")} subtitle={t("mod.subtitle")} />

      <PointsSettings />

      <Card className="bg-[#10121B] border-white/5">
        <CardContent className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <label className="text-slate-300 text-sm">{t("mod.batch")}</label>
            <select data-testid="mod-batch-filter" value={filters.batch_id} onChange={(e) => setFilters({ ...filters, batch_id: e.target.value })}
              className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
              <option value="">{t("mod.all")}</option>
              {(access?.batches || []).map((b) => <option key={b.batch_id} value={b.batch_id}>{b.code} — {b.name}</option>)}
            </select>
          </div>
          <DateField label={t("mod.from")} testId="mod-date-from" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} />
          <DateField label={t("mod.to")} testId="mod-date-to" value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} />
          <div className="space-y-1.5">
            <label className="text-slate-300 text-sm">{t("mod.onlyHidden")}</label>
            <div className="flex gap-2">
              <select data-testid="mod-hidden-filter" value={filters.hidden} onChange={(e) => setFilters({ ...filters, hidden: e.target.value })}
                className="flex-1 h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
                <option value="">{t("mod.all")}</option>
                <option value="ONLY">{t("mod.hidden")}</option>
              </select>
              <Button data-testid="mod-reset-btn" variant="ghost" onClick={() => setFilters(EMPTY)} className="text-slate-400 hover:bg-white/5 h-10 px-3">
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div data-testid="mod-count" className="text-xs text-slate-400">{entries.length} {t("mod.results")}</div>

      {entries.length === 0 ? <EmptyState testId="mod-empty" text={t("mod.empty")} /> : (
        <div className="space-y-3">
          {entries.map((e) => (
            <Card key={e.entry_id} data-testid={`mod-entry-${e.entry_id}`} className={`bg-[#161923] border-white/5 ${e.is_hidden ? "opacity-70" : ""}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
                {e.photo_file_id && <ProofImage fileId={e.photo_file_id} testId={`mod-photo-${e.entry_id}`} className="h-24 w-24" />}
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white truncate">{e.member_name || "—"}</span>
                    <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">{e.author_role || "MEMBER"}</span>
                    {e.batch_code && <span className="text-[11px] text-slate-500">{e.batch_code}</span>}
                    {e.is_hidden && (
                      <span data-testid={`mod-hidden-badge-${e.entry_id}`} className="rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold text-red-300 inline-flex items-center gap-1">
                        <ShieldAlert className="h-3 w-3" /> {t("mod.hidden")}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500">{formatDateID(e.date, lang)}{e.meal_type ? ` · ${t(`food.meal_${e.meal_type}`)}` : ""}</div>
                  {e.note && <p className="text-sm text-slate-300 line-clamp-3">{e.note}</p>}
                  <div className="text-[11px] text-slate-500">{e.comment_total} · {e.reaction_total}</div>
                </div>
                <Button data-testid={`mod-toggle-${e.entry_id}`} disabled={busy} onClick={() => toggleHidden(e)}
                  className={`h-9 shrink-0 ${e.is_hidden ? "bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300" : "bg-white/10 hover:bg-white/15 text-white"}`}>
                  {e.is_hidden ? <><Eye className="h-4 w-4 mr-1" /> {t("mod.unhide")}</> : <><EyeOff className="h-4 w-4 mr-1" /> {t("mod.hide")}</>}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function PointsSettings() {
  const { t } = useLang();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/journal-points").then(({ data }) => setForm(data.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/journal-points", {
        food_journal_post_points: Number(form.food_journal_post_points),
        education_post_points: Number(form.education_post_points),
        comment_points: Number(form.comment_points),
        reaction_points: Number(form.reaction_points),
      });
      setForm(data.data);
      toast.success(t("mod.pointsSaved"));
    } catch (err) { toast.error(extractError(err).message); }
    finally { setBusy(false); }
  };

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Card data-testid="mod-points-settings" className="bg-[#10121B] border-white/5">
      <CardContent className="p-5">
        <form onSubmit={save} className="space-y-4">
          <h2 className="text-base md:text-lg font-semibold text-white">{t("mod.pointsTitle")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Field label={t("mod.foodPost")} testId="mod-points-food" type="number" min="0" value={form.food_journal_post_points} onChange={set("food_journal_post_points")} />
            <Field label={t("mod.eduPost")} testId="mod-points-edu" type="number" min="0" value={form.education_post_points} onChange={set("education_post_points")} />
            <Field label={t("mod.comment")} testId="mod-points-comment" type="number" min="0" value={form.comment_points} onChange={set("comment_points")} />
            <Field label={t("mod.reaction")} testId="mod-points-reaction" type="number" min="0" value={form.reaction_points} onChange={set("reaction_points")} />
          </div>
          <div className="flex justify-end">
            <Button data-testid="mod-points-save-btn" type="submit" disabled={busy} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold h-10">
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t("common.loading")}</> : t("common.save")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
